import { ChangeEvent, useCallback, useState } from 'react';
import { useI18n } from '@hooks/useI18n/useI18n';
import { Endpoints } from '@utils/enums';
import { UploadDocumentType } from './enums';
import {
  IDependencyValues,
  IDocumentType,
  IUseDocumentTypeReturn,
} from './types';

const getDocumentTypes = ({
  hasDownloadedAgreement = false,
  hasUploadedSignedAgreement = false,
  isManualIdentityDocumentUploadRequired = false,
  isManualSigningRequired = false,
}: IDependencyValues): IDocumentType[] => [
  {
    id: UploadDocumentType.UploadCustomerAgreement,
    name: 'LABEL.CUSTOMER_AGREEMENT',
    description: 'PAGE.ORDERS.UPLOAD_CUSTOMER_AGREEMENT_DESCRIPTION',
    endpoint: Endpoints.CustomerAgreement,
    dependencies: [
      isManualSigningRequired,
      hasDownloadedAgreement,
      !hasUploadedSignedAgreement,
    ],
  },
  {
    id: UploadDocumentType.UploadIdDocuments,
    name: 'LABEL.CUSTOMER_ID',
    description: 'PAGE.ORDERS.UPLOAD_CUSTOMER_ID_DESCRIPTION',
    endpoint: Endpoints.CustomerIdUpload,
    dependencies: [isManualIdentityDocumentUploadRequired],
    uploadTitle: 'LABEL.CUSTOMER_ID_FRONT_AND_BACK',
    allowMultipleFiles: true,
  },
];

interface IUseDocumentType {
  dependencyValues: IDependencyValues;
}

export const useDocumentType = ({
  dependencyValues,
}: IUseDocumentType): IUseDocumentTypeReturn => {
  const { translate } = useI18n();

  const documentTypes = getDocumentTypes(dependencyValues);
  const getDocumentTypeName = (name: string) => translate(name.toUpperCase());

  const availableDocumentTypes = [...documentTypes]
    .filter(docType => docType.dependencies.every(Boolean))
    .sort((a, b) =>
      getDocumentTypeName(a.name).localeCompare(getDocumentTypeName(b.name)),
    );

  const documentTypeOptions = availableDocumentTypes.map(docType => ({
    id: docType.id,
    name: getDocumentTypeName(docType.name),
  }));

  const [documentTypeId, setDocumentTypeId] = useState<UploadDocumentType>();

  const handleChangeDocumentType = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const selectedDocumentTypeId = event.target.value;
      setDocumentTypeId(selectedDocumentTypeId as UploadDocumentType);
    },
    [],
  );

  return {
    documentTypeOptions,
    documentTypes,
    handleChangeDocumentType,
    setDocumentTypeId,
    documentTypeId,
  };
};
